import React from 'react';
import { Box, Typography, Container, Grid, Link, IconButton, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { LogoIcon } from '../../tool';
import { useInstitutions } from '../../../hooks/useInstitutions';

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

const NAV = [
  { label: 'Inicio',      id: 'hero' },
  { label: 'Servicios',   id: 'features' },
  { label: 'Institución', id: 'about' },
  { label: 'Contacto',    id: 'contact' },
];

const LandingFooter = () => {
  const theme = useTheme();
  const { institutions } = useInstitutions();
  const inst = institutions?.[0] ?? null;

  // Usa primary.darker si está definido en la paleta, si no cae a primary.dark
  const footerBg = theme.palette.primary.darker ?? theme.palette.primary.dark;

  return (
    <Box sx={{ bgcolor: footerBg, color: 'rgba(255,255,255,0.65)', pt: { xs: 10, md: 14 }, pb: 6, position: 'relative', overflow: 'hidden' }}>
      {/* Subtle overlay */}
      <Box sx={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(160deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 100%)',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6}>

          {/* Brand col */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              {inst?.logo
                ? <Box component="img" src={inst.logo} alt="Logo" sx={{ width: 34, height: 34, objectFit: 'contain' }} />
                : <LogoIcon sx={{ fontSize: 34, color: 'primary.light' }} />}
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1rem', letterSpacing: 0.3 }}>
                {inst?.name ?? 'AGUA POTABLE'}
              </Typography>
            </Box>

            {inst?.about && (
              <Typography variant="body2" sx={{ lineHeight: 1.85, mb: 4, maxWidth: 340, fontSize: '0.87rem' }}>
                {inst.about.length > 160 ? inst.about.slice(0, 160).trimEnd() + '…' : inst.about}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {inst?.facebookUrl && (
                <SocialBtn href={inst.facebookUrl} label="Facebook" icon={<FacebookIcon sx={{ fontSize: 18 }} />} theme={theme} />
              )}
              {inst?.whatsApp && (
                <SocialBtn href={`https://wa.me/${inst.whatsApp.replace(/\D/g, '')}`} label="WhatsApp" icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} theme={theme} />
              )}
            </Box>

            {inst?.taxId && (
              <Typography variant="caption" sx={{ display: 'block', mt: 4, color: 'rgba(255,255,255,0.22)', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                RUC / NIT: {inst.taxId}
              </Typography>
            )}
          </Grid>

          {/* Nav col */}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.9)', fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.66rem', mb: 3 }}>
              Secciones
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {NAV.map((item) => (
                <Link
                  key={item.id}
                  component="button"
                  onClick={() => scrollTo(item.id)}
                  underline="none"
                  sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.87rem', fontWeight: 500, textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', p: 0, '&:hover': { color: 'primary.light' }, transition: 'color 0.2s' }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Contact col */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.9)', fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.66rem', mb: 3 }}>
              Información
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {[
                { icon: <LocationOnOutlinedIcon sx={{ fontSize: 15 }} />, v: inst?.direction,    href: null },
                { icon: <LocalPhoneOutlinedIcon sx={{ fontSize: 15 }} />, v: inst?.telephone ? String(inst.telephone) : null, href: inst?.telephone ? `tel:${inst.telephone}` : null },
                { icon: <EmailOutlinedIcon      sx={{ fontSize: 15 }} />, v: inst?.email,        href: inst?.email ? `mailto:${inst.email}` : null },
                { icon: <AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />, v: inst?.businessHours, href: null },
              ]
                .filter((c) => c.v)
                .map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ color: 'primary.light', flexShrink: 0, mt: '1px' }}>{c.icon}</Box>
                    {c.href ? (
                      <Link href={c.href} underline="none" sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.87rem', wordBreak: 'break-word', '&:hover': { color: 'primary.light' }, transition: 'color 0.2s' }}>
                        {c.v}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.87rem', wordBreak: 'break-word' }}>
                        {c.v}
                      </Typography>
                    )}
                  </Box>
                ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 8, borderColor: 'rgba(255,255,255,0.06)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.25)' }}>
            © {new Date().getFullYear()} {inst?.name ?? 'AGUA POTABLE'} — Todos los derechos reservados.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            {['Términos', 'Privacidad'].map((label) => (
              <Link key={label} href="#" underline="hover" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.77rem', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}>
                {label}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const SocialBtn = ({ href, label, icon, theme }) => (
  <IconButton
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    size="small"
    sx={{
      width: 36, height: 36,
      borderRadius: '8px',
      color: 'rgba(255,255,255,0.45)',
      bgcolor: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.08)',
      '&:hover': {
        color: 'primary.light',
        bgcolor: alpha(theme.palette.primary.main, 0.15),
        borderColor: alpha(theme.palette.primary.main, 0.35),
      },
      transition: 'all 0.2s',
    }}
  >
    {icon}
  </IconButton>
);

export default LandingFooter;
